import { useCallback, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';

import { Box } from 'approot/shared/design-system/box/box.style';
import type { GetLessonTeachingAndLearningSequenceQuery } from '../teaching-notes.graphql.generated';
import { ENGLISH, MATHS } from 'global/constants';
import { InquisitiveSubject } from 'global/graphql/scalars.type';
import TeachingLearningStandards from 'approot/shared/teaching-learning-sequence/teaching-learning-standards';
import {
  ENGLISH_CONNECTION,
  MATH_CONNECTION,
  TeachingAndLearningOutcome,
} from 'approot/shared/teaching-learning-sequence/teaching-learning-sequence.constants';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { useGetDraftActivitiesFromCache } from 'approot/activity-builder/logic/hooks/use-get-draft-activities-from-cache';
import { DraftLessonActivities } from 'approot/activity-builder/activity-builder-draft.constants';
import type { Pluck } from 'lib/gql/pluck';

const ExtraConnectionsEdit = ({
  note,
  onChange,
}: {
  note: Pluck<
    GetLessonTeachingAndLearningSequenceQuery,
    'getLessonTeachingAndLearningSequence'
  >;
  onChange: (
    note: Pluck<
      GetLessonTeachingAndLearningSequenceQuery,
      'getLessonTeachingAndLearningSequence'
    >
  ) => void;
}) => {
  const { contentId, contentType } = useGetInteractiveLessonParams();
  const data = useGetDraftActivitiesFromCache({
    contentId,
    contentType,
  });

  const years = useMemo(() => {
    return (data as DraftLessonActivities)?.lesson?.years.map(y => y.name);
  }, [data]);

  const handleNoteConnectionChange = useCallback(
    (
      note: Pluck<
        GetLessonTeachingAndLearningSequenceQuery,
        'getLessonTeachingAndLearningSequence'
      >,
      subject: InquisitiveSubject,
      connections: TeachingAndLearningOutcome[]
    ) => {
      const noteCopy = cloneDeep(note);
      const group = noteCopy.connections?.find(c => c.subject === subject);

      if (!group) {
        if (noteCopy.connections) {
          noteCopy.connections.push({
            subject,
            outcomes: connections,
            __typename: 'OutcomesBySubject',
          });
        } else {
          noteCopy.connections = [
            {
              subject,
              outcomes: connections,
              __typename: 'OutcomesBySubject',
            },
          ];
        }
      } else if (group) {
        group.outcomes = connections;
      }

      onChange({
        ...noteCopy,
      });
    },
    [onChange]
  );

  return (
    <Box $marginTop={24} $marginBottom={24}>
      {years && (
        <>
          <TeachingLearningStandards
            label={ENGLISH_CONNECTION}
            savedOutcomes={
              note.connections?.find(c => c.subject === ENGLISH)?.outcomes || []
            }
            subject={ENGLISH}
            years={years}
            onChange={connections => {
              handleNoteConnectionChange(note, ENGLISH, connections);
            }}
          />
          <TeachingLearningStandards
            label={MATH_CONNECTION}
            savedOutcomes={
              note.connections?.find(c => c.subject === MATHS)?.outcomes || []
            }
            subject={MATHS}
            years={years}
            onChange={connections => {
              handleNoteConnectionChange(note, MATHS, connections);
            }}
          />
        </>
      )}
    </Box>
  );
};

export default ExtraConnectionsEdit;
